import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usePrint } from '../../context/PrintContext';
import { CheckCircle2, PlayCircle, FilePlus } from 'lucide-react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';

function ExamListModal({ isOpen, onClose, examList, stampId, tree_name, edu_ver, student_name, onWrongAnswerGenerated }) {
  const [selectedExams, setSelectedExams] = useState([]);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const { setPrintData } = usePrint();
  const [selectAll, setSelectAll] = useState(false);

  if (!isOpen) return null;

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrint = async () => {
    if (selectedExams.length === 0) {
      alert('시험을 선택해주세요.');
      return;
    }

    if (selectedExams.length > 100) {
      alert('시험은 최대 100개까지만 선택 가능합니다.');
      return;
    }

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            stamp_id: parseInt(stampId),
            exam_ids: selectedExams.join(',')
          })
        }
      );

      const errorData = await response.json();

      if (errorData.status_code === 422) {
        alert('선택한 시험에 오답이 없어 오답노트를 생성할 수 없습니다.');
        return;
      }

      if (!response.ok) {
        if (response.status === 403) {
          alert('권한이 없습니다.');
        } else if (errorData.status_code === 409) {
          alert('선택한 시험 중 처리할 수 없는 시험이 포함되어 있습니다.');
        } else {
          alert('오답노트 생성 중 오류가 발생했습니다.');
        }
        return;
      }

      onWrongAnswerGenerated({
        exam_ids: selectedExams,
        wrong_count: Math.floor(Math.random() * 20) + 1, // 임시 데이터
        total_count: Math.floor(Math.random() * 50) + 20, // 임시 데이터
      });
      onClose();

    } catch (error) {
      console.error('오답 수집 중 오류 발생:', error);
      alert('오답 수집 중 오류가 발생했습니다.');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedExams(examList.map(exam => exam.exam_id));
      setSelectAll(true);
    } else {
      setSelectedExams([]);
      setSelectAll(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="오답노트 생성"
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>닫기</Button>
          <Button onClick={handlePrint}>선택 확인</Button>
        </>
      }
    >
      <div className="mb-4 flex items-center">
        <label className="flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
            className="mr-2 size-5"
          />
          <span className="select-none text-sm font-medium">전체 선택</span>
        </label>
      </div>

      <div className="space-y-3">
          {examList.map((exam) => {
            const isSelected = selectedExams.includes(exam.exam_id);
            return (
              <div
                key={exam.exam_id}
                className={`rounded-3xl border border-gray-200 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20 cursor-pointer ${isSelected ? 'ring-2 ring-primary/30 border-primary/30' : ''}`}
                onClick={(e) => {
                  if (!e.target.matches('input[type="checkbox"]')) {
                    setSelectedExams(prev => {
                      const already = prev.includes(exam.exam_id);
                      const next = already ? prev.filter(id => id !== exam.exam_id) : [...prev, exam.exam_id];
                      setSelectAll(next.length === examList.length);
                      return next;
                    });
                  }
                }}
              >
                <div className="px-4 pt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          setSelectedExams(prev => {
                            const next = e.target.checked ? [...prev, exam.exam_id] : prev.filter(id => id !== exam.exam_id);
                            setSelectAll(next.length === examList.length);
                            return next;
                          });
                        }}
                        className="size-5"
                      />
                      {exam.exam_status === 999 ? (
                        <CheckCircle2 className="text-green-600" size={28} />
                      ) : exam.exam_status === 400 ? (
                        <PlayCircle className="text-orange-500" size={28} />
                      ) : (
                        <FilePlus className="text-blue-500" size={28} />
                      )}
                      <div>
                        <h3 className="text-[14px] font-semibold">TID: {exam.exam_id}</h3>
                        <p className="text-xs text-gray-500">테스트 {exam.exam_type}</p>
                      </div>
                    </div>
                    <div className="rounded-3xl text-[9px] font-medium text-gray-500">
                      <div>생성: {new Date(exam.exam_created_at).toLocaleDateString()}</div>
                      <div>수정: {new Date(exam.exam_updated_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
                <div className="px-[14.4px] pb-[14.4px]">
                  <div className="grid grid-cols-4 gap-1 text-xs">
                    <div className="rounded-3xl bg-gray-50 p-2 text-center">
                      <div className="text-gray-500">TID</div>
                      <div className="flex flex-col items-center">
                        <div className="flex items-center justify-center gap-1">
                          <div className="text-sm font-semibold text-gray-800">{exam.exam_id}</div>
                          <span className="text-sm font-medium text-green-600">
                            {exam.exam_type === 'alpha' ? 'α' :
                             exam.exam_type === 'beta' ? 'β' :
                             exam.exam_type === 'gamma' ? 'γ' :
                             exam.exam_type === 'delta' ? 'δ' :
                             exam.exam_type === 'custom' ? '𝐂' :
                             exam.exam_type === 'epsilon' ? 'ε' :
                             exam.exam_type || '-'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">(0회)</div>
                      </div>
                    </div>
                    <div className="rounded-3xl bg-gray-50 p-2 text-center">
                      <div className="text-gray-500">난이도</div>
                      <div className="flex flex-col items-center justify-center flex-1">
                        <div className="text-sm font-semibold text-gray-800">
                          {exam.exam_difficulty || '-'}
                        </div>
                      </div>
                    </div>
                    <div className="rounded-3xl bg-gray-50 p-2 text-center">
                      <div className="text-gray-500">문제수</div>
                      <div className="flex flex-col items-center justify-center flex-1">
                        <div className="text-sm font-semibold text-gray-800">
                          {exam.problems_count || 0}
                        </div>
                      </div>
                    </div>
                    <div className="rounded-3xl bg-gray-50 p-2 text-center">
                      <div className="text-gray-500">상태</div>
                      <div className="flex flex-col items-center">
                        <div className={`text-sm font-semibold ${
                          exam.exam_status === 999 ? 'text-green-600' :
                          exam.exam_status === 400 ? 'text-orange-500' :
                          exam.exam_status === 200 ? 'text-blue-500' :
                          'text-gray-500'
                        }`}>
                          {exam.exam_status === 200 ? '결과입력필요' :
                           exam.exam_status === 400 ? '결과입력완료' :
                           exam.exam_status === 999 ? '결과확정완료' : '상태 미정'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </Modal>
  );
}

export default ExamListModal; 